import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { useProfileStore } from '@/stores/profileStore';
import { apiService } from '@/services/api';
import { trackUsageEvent } from '@/services/usageEvent';
import type { FanZoneComment } from '@/types';

interface FanZonePanelProps {
  matchId: string;
  /** Affiche le pied de saisie (défaut true). */
  showComposer?: boolean;
}

function formatCommentDate(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `Il y a ${diffDays} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export function FanZonePanel({ matchId, showComposer = true }: FanZonePanelProps) {
  const colors = useTheme();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { pseudo, tifosiCle, loadProfile } = useProfileStore();

  const [comments, setComments] = useState<FanZoneComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadComments = useCallback(async () => {
    if (!matchId) return;
    setError(null);
    try {
      const result = await apiService.getFanZoneCommentsByMatch(matchId);
      setComments(result.data);
    } catch {
      setError('Impossible de charger les commentaires.');
    } finally {
      setIsLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    setIsLoading(true);
    void loadComments();
  }, [loadComments]);

  useEffect(() => {
    if (!matchId || !tifosiCle) return;
    trackUsageEvent(
      { type: 'fanzone_opened', tifosi: tifosiCle, match_football: matchId },
      { navigationType, dedupeContext: matchId },
    );
  }, [matchId, tifosiCle, navigationType]);

  const handleSendComment = async () => {
    const text = newComment.trim();
    if (!text || !matchId || isSending) return;
    if (!tifosiCle) {
      navigate('/login');
      return;
    }
    setIsSending(true);
    const author = pseudo?.trim() || 'Supporteur';
    const payload = {
      cle: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      message: text,
      match_football: matchId,
      tifosi: tifosiCle,
    };
    const optimistic: FanZoneComment = {
      id: payload.cle,
      cle: payload.cle,
      matchId,
      tifosi: tifosiCle,
      author,
      text,
      createdAt: new Date().toISOString(),
    };
    setComments((prev) => [...prev, optimistic]);
    setNewComment('');
    try {
      await apiService.createFanZoneComment(payload);
      trackUsageEvent(
        { type: 'fanzone_comment', tifosi: tifosiCle, match_football: matchId },
        { skipOnPop: false, dedupeMs: 0, dedupeContext: payload.cle },
      );
      await loadComments();
    } catch (e) {
      console.error('Fan zone send error:', e);
      setComments((prev) => prev.filter((c) => c.id !== optimistic.id));
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 24, gap: 12 }}>
        <Icon name="alert-circle" size={40} color={Colors.primary} />
        <span style={{ fontSize: 14, color: colors.text, textAlign: 'center' }}>{error}</span>
        <button
          type="button"
          onClick={() => {
            setIsLoading(true);
            void loadComments();
          }}
          style={{
            backgroundColor: Colors.primary,
            padding: '10px 20px',
            borderRadius: 24,
            border: 'none',
            cursor: 'pointer',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: showComposer ? 8 : 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => void loadComments()}
            aria-label="Actualiser la Fan Zone"
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              cursor: 'pointer',
            }}
          >
            <Icon name="refresh" size={16} color={colors.text} />
          </button>
        </div>

        {comments.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 48, paddingBottom: 48, gap: 8 }}>
            <Icon name="chatbubble-outline" size={40} color={colors.textSecondary} />
            <span style={{ fontSize: 16, fontWeight: 600, color: colors.textSecondary }}>Soyez le premier à réagir !</span>
            <span style={{ fontSize: 13, textAlign: 'center', padding: '0 32px', color: colors.textSecondary }}>
              Partagez vos réactions sur le match avec les autres supporters.
            </span>
          </div>
        ) : (
          comments.map((c) => (
            <div
              key={c.id}
              style={{
                display: 'flex',
                padding: 12,
                borderRadius: 16,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${Colors.primary}20`,
                  marginRight: 12,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 700, color: Colors.primary }}>
                  {c.author.charAt(0).toUpperCase()}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{c.author}</span>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>{formatCommentDate(c.createdAt)}</span>
                </div>
                <span style={{ fontSize: 14, lineHeight: '20px', color: colors.text }}>{c.text}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {showComposer ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            paddingTop: 12,
            gap: 10,
            borderTop: `1px solid ${colors.border}`,
            paddingBottom: 'max(4px, env(safe-area-inset-bottom))',
          }}
        >
          <textarea
            style={{
              flex: 1,
              minHeight: 44,
              maxHeight: 100,
              borderRadius: 22,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 12,
              paddingBottom: 12,
              fontSize: 15,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
              color: colors.text,
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
            }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={tifosiCle ? 'Ajouter un commentaire...' : 'Connectez-vous pour commenter'}
            maxLength={500}
            disabled={isSending || !tifosiCle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSendComment();
              }
            }}
          />
          <button
            type="button"
            onClick={() => void handleSendComment()}
            disabled={!newComment.trim() || isSending || !tifosiCle}
            style={{
              width: 44,
              height: 44,
              borderRadius: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: newComment.trim() && tifosiCle ? Colors.primary : colors.border,
              cursor: newComment.trim() && tifosiCle ? 'pointer' : 'default',
              flexShrink: 0,
              border: 'none',
            }}
          >
            {isSending ? (
              <div className="spinner spinner-small" style={{ borderTopColor: '#fff' }} />
            ) : (
              <Icon name="send" size={20} color="#ffffff" />
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
}
